import { useState, useEffect } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Toast, NavBar, PageHeader } from '@/components/ui';
import {
  ProfileCard,
  BackgroundImageSection,
  AccountInfoCard,
  SkillsCard,
  CertificatesCard,
  NotificationsCard,
  DeleteAccountSection,
  DeleteAccountModal,
  SaveCancelBar,
} from './components';
import { useSettingsForm } from './useSettingsForm';

export function SettingsPage() {
  const form = useSettingsForm();
  const { user, isVolunteer, isDataLoaded, dirty, showDeleteModal, setShowDeleteModal, isDeletingAccount, handleDeleteAccount, toast, hideToast } = form;
  const [showBack, setShowBack] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const q = window.matchMedia('(max-width: 767px)');
    const update = () => setShowBack(q.matches);
    update();
    q.addEventListener('change', update);
    return () => q.removeEventListener('change', update);
  }, []);

  if (!user) {
    return (
      <Box
        minH="100vh"
        bg="white"
        pb={{ base: 'var(--mobile-bottom-safe)', lg: '6' }}
        pl={{ lg: 'var(--sidebar-width)' }}
        transition={{ lg: 'padding-left 0.3s cubic-bezier(0.4,0,0.2,1)' }}
      >
        <Box maxW={{ base: '100%', md: '920px', xl: '1040px' }} mx="auto" px={{ base: '5', md: '6' }} py="5">
          <Text textAlign="center" color="volyx.textSecondary" fontSize="md" mt="10">
            Loading...
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      bg="white"
      pb={{ base: 'var(--mobile-bottom-safe)', lg: '6' }}
      pl={{ lg: 'var(--sidebar-width)' }}
      transition={{ lg: 'padding-left 0.3s cubic-bezier(0.4,0,0.2,1)' }}
    >
      <Box
        maxW={{ base: '100%', md: '920px', xl: '1040px' }}
        mx="auto"
        px={{ base: '5', md: '6' }}
        py="5"
        pb={{ base: dirty && isDataLoaded ? '120px' : '5', lg: '5' }}
      >
        <PageHeader title="Settings" showBack={showBack} showMobileLogo={false} />

        <Flex flexDir="column" gap="6">
          {/* Row 1: Profile (square) + Background (longer) - side by side on 1024px+ */}
          <Flex
            flexDir={{ base: 'column', lg: 'row' }}
            gap="6"
            align={{ lg: 'stretch' }}
          >
            <Box w={{ lg: '240px' }} flexShrink={{ lg: 0 }}>
              <ProfileCard form={form} />
            </Box>
            <Box flex={{ lg: '1' }} minW={{ lg: '0' }}>
              <BackgroundImageSection form={form} />
            </Box>
          </Flex>

          {/* Row 2: Account Info left, Skills+Certificates right on 1024px+ */}
          <Flex
            flexDir={{ base: 'column', lg: isVolunteer ? 'row' : 'column' }}
            gap="6"
            align={{ lg: isVolunteer ? 'flex-start' : undefined }}
          >
            <Box
              flex={{ lg: isVolunteer ? '1' : undefined }}
              minW={{ lg: isVolunteer ? '0' : undefined }}
            >
              <AccountInfoCard form={form} />
            </Box>
            {isVolunteer && (
              <Flex
                flex={{ lg: '1' }}
                minW={{ lg: '0' }}
                flexDir="column"
                gap="6"
              >
                <SkillsCard form={form} />
                <CertificatesCard form={form} />
              </Flex>
            )}
          </Flex>

          <NotificationsCard />

          <DeleteAccountSection onDeleteClick={() => setShowDeleteModal(true)} />
        </Flex>

        {dirty && isDataLoaded && <SaveCancelBar form={form} />}

        <DeleteAccountModal
          isOpen={showDeleteModal}
          isDeleting={isDeletingAccount}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
        />

        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        <NavBar />
      </Box>
    </Box>
  );
}
